<template>
    <div
        :class="{[`calendar-${size}`]: true}"
        class="calendar"
    >
        <div class="calendar-nav navbar">
            <base-button
                flavor="link"
                size="lg"
                class="btn-action"
                tabindex="-1"
                @click.prevent="previous_month"
            >
                <open-icon
                    glyph="angle-left"
                    :title="$root.translate('Previous month')"
                />
            </base-button>
            <div
                v-if="current_page_timestamp"
                class="navbar-primary"
            >
                {{ current_page_date | nibnut.date("MMM y") }}
            </div>
            <base-button
                flavor="link"
                size="lg"
                class="btn-action"
                tabindex="-1"
                @click.prevent="next_month"
            >
                <open-icon
                    glyph="angle-right"
                    :title="$root.translate('Next month')"
                />
            </base-button>
        </div>

        <div class="calendar-container">
            <div class="calendar-header">
                <div
                    v-for="weekday in weekdays"
                    :key="weekday"
                    class="calendar-date"
                >
                    {{ weekday }}
                </div>
            </div>

            <div class="calendar-body">
                <div
                    v-for="grid_data in grid"
                    :key="grid_data.id"
                    :class="grid_data.cell_classes"
                    class="calendar-date text-center"
                >
                    <base-button
                        :disabled="grid_data.disabled"
                        :class="grid_data.button_classes"
                        class="date-item"
                        tabindex="-1"
                        @click.prevent="click(grid_data.date)"
                    >
                        {{ grid_data.date | nibnut.date("d") }}
                    </base-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script type="text/javascript">
import {
    startOfToday,
    getTime,
    subDays,
    addDays,
    subMonths,
    addMonths,
    fromUnixTime,
    getDay,
    getMonth,
    startOfMonth,
    isEqual,
    isAfter,
    isBefore,
    isSameDay
} from "date-fns"

import handles_dates from "@/nibnut/mixins/HandlesDates"

import BaseButton from "@/nibnut/components/Buttons/BaseButton"
import OpenIcon from "@/nibnut/components/OpenIcon"

export default {
    name: "BaseCalendar",
    mixins: [handles_dates],
    components: {
        BaseButton,
        OpenIcon
    },
    mounted () {
        this.reset()
    },
    watch: {
        selection: "reset"
    },
    methods: {
        reset () {
            let first_selected_date
            if(!this.selection) first_selected_date = startOfToday()
            else if(Array.isArray(this.selection)) first_selected_date = this.standardized_range_object(this.selection[0]).from
            else first_selected_date = this.standardized_range_object(this.selection).from
            this.current_page_timestamp = getTime(startOfMonth(first_selected_date))
        },
        previous_month () {
            this.current_page_timestamp = getTime(subMonths(this.current_page_date, 1))
        },
        next_month () {
            this.current_page_timestamp = getTime(addMonths(this.current_page_date, 1))
        },
        click (date) {
            this.$emit("click", date)
        }
    },
    computed: {
        current_page_date () {
            return fromUnixTime(this.current_page_timestamp / 1000)
        },
        weekdays () {
            return [
                this.$root.translate("Sun"),
                this.$root.translate("Mon"),
                this.$root.translate("Tue"),
                this.$root.translate("Wed"),
                this.$root.translate("Thu"),
                this.$root.translate("Fri"),
                this.$root.translate("Sat")
            ]
        },
        grid () {
            const today = startOfToday()
            const min = this.standardized_date(this.min)
            const max = this.standardized_date(this.max)
            let selection = this.selection
            if(selection && !Array.isArray(selection)) selection = [selection]

            const dates = []
            if(this.current_page_timestamp) {
                let date = subDays(this.current_page_date, getDay(this.current_page_date))
                const current_month = getMonth(this.current_page_date)

                for(let loop = 0; loop < 35; loop++) {
                    const month = getMonth(date)
                    const cell_classes = {}
                    if((month < current_month) || ((month === 11) && (current_month === 0))) cell_classes["prev-month"] = true
                    else if((month > current_month) || ((month === 0) && (current_month === 12))) cell_classes["next-month"] = true

                    if(selection) {
                        selection.forEach(selected_date => {
                            const range = this.standardized_range_object(selected_date)
                            if(isEqual(date, range.from) || (isAfter(date, range.from) && isBefore(date, range.to))) {
                                if(!cell_classes["calendar-range"]) cell_classes["calendar-range"] = true
                                if(!cell_classes["range-start"] && isSameDay(date, range.from)) cell_classes["range-start"] = true
                                if(!cell_classes["range-end"] && isSameDay(date, range.to)) cell_classes["range-end"] = true
                            }
                        })
                    }

                    const button_classes = {}
                    if(isSameDay(date, today)) button_classes["date-today"] = true

                    const disabled = (min && isBefore(date, min)) || (max && isAfter(date, max))

                    dates.push({
                        id: getTime(date),
                        date,
                        cell_classes,
                        button_classes,
                        disabled
                    })
                    date = addDays(date, 1)
                }
            }
            return dates
        }
    },
    props: {
        min: { // null, string or native date
            default: null
        },
        max: { // null, string or native date
            default: null
        },
        selection: { // null, [dates or ranges], date or range ; date is native date or string, range is {from: <date>, to: <date>}
            default: null
        },
        size: {
            type: String,
            validator: prop => !!prop && prop.match(/^(sm|md|lg)$/i),
            default: "md"
        }
    },
    data () {
        return {
            current_page_timestamp: null // stores the first day of the month being viewed, **as a timestamp*** so it is reactive
        }
    }
}
</script>

<style lang="scss">
@import "@/assets/sass/variables";

.calendar {
    .calendar-body {
        background-color: $body-bg;
    }

    &.calendar-sm {
        .calendar-nav {
            padding: $layout-spacing-sm;
        }

        .calendar-header,
        .calendar-body {
            padding: $layout-spacing-sm 0;
        }

        .calendar-date {
            padding: div($unit-1, 2);
        }
    }
}
</style>
